import { Component, inject, signal, computed, effect, input } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ContainerId } from '../../../models/drawer';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PATH_RELEASE_PAGES, PATH_MORE_PROVIDER_ORDER_IDS } from 'src/app/global';
import { ProviderOrder } from 'src/app/calvin-emr-shared/models/provider-order.model';
import { PatientService } from 'src/app/calvin-emr-shared/services/patient.service';
import { PageComponent } from 'src/app/calvin-emr-shared/page/page.component';
import { COLLECTION_PATIENTS, COLLECTION_STUDENT_PATIENTS } from 'src/app/calvin-emr-shared/global';
import { ContainersService } from 'src/app/knightmeds/services/containers.service';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { ProgressBar } from 'primeng/progressbar';
import { KnightmedsDiagramComponent } from 'src/app/knightmeds/knightmeds-diagram/knightmeds-diagram.component';
import { Checkbox } from 'primeng/checkbox';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonDirective } from 'primeng/button';

/** A page displayed when a medication is released */
@Component({
  selector: 'app-release-page',
  templateUrl: './release-page.component.html',
  styleUrls: ['./release-page.component.scss'],
  standalone: true,
  imports: [
    ProgressBar,
    RouterLink,
    KnightmedsDiagramComponent,
    Checkbox,
    FormsModule,
    ReactiveFormsModule,
    SelectButtonModule,
    ButtonDirective,
  ],
})
export class ReleasePageComponent extends PageComponent {
  /** The patient with the provider order this medication is being released for */
  private patientSignal = signal<Patient | null>(null);
  readonly patient = computed(() => this.patientSignal());

  /** The provider order this medication is being released for */
  private providerOrderSignal = signal<ProviderOrder | null>(null);
  readonly providerOrder = computed(() => this.providerOrderSignal());

  /**
   * The id of the container that was opened to release the medication
   * undefined indicates no container was opened
   */
  private openedContainerIdSignal = signal<ContainerId | undefined>(undefined);
  readonly openedContainerId = computed(() => this.openedContainerIdSignal());

  /** The currently highlighted container id in the diagram */
  highlightedContainerId = signal<ContainerId | undefined>(undefined);

  /** The provider order to be released on the next page */
  private nextProviderOrderIdSignal = signal<string | undefined>(undefined);
  readonly nextProviderOrderId = computed(() => this.nextProviderOrderIdSignal());

  /** The query param for the next page */
  private moreProviderOrderIdsSignal = signal<{ [moreProviderOrderIds: string]: string[] } | undefined>(undefined);
  readonly moreProviderOrderIds = computed(() => this.moreProviderOrderIdsSignal());

  /** Whether any containers are open */
  anyContainersOpen = false;

  /** Whether all necessary data has loaded */
  readonly loaded = computed(() =>
    !!this.patientSignal() && !!this.providerOrderSignal() && this.openedContainerIdSignal() !== undefined
  );

  // These are ngModels for the two-RN-check on controlled substances.
  verifyControlledMedsRemaining = false;
  isThereWaste = false;
  secondRNsignature = '';
  secondRNwasteAmt = '';
  yesNo = [{ label: 'No', value: false }, { label: 'Yes', value: true }];

  quantityRemaining: number | null = null;
  containerQuantity: number | null = null;

  readonly patientId = input<string>(undefined, { alias: 'patient-id' });
  readonly providerOrderId = input<string>(undefined, { alias: 'medication-id' });

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private patientService = inject(PatientService);
  private containersService = inject(ContainersService);

  constructor() {
    super();

    // Wait for user to load, then validate and fetch patient
    effect(() => {
      const user = this.authStore.user();
      if (!user) return;

      const patientId = this.patientId();
      if (!patientId || !user.patientIds.includes(patientId)) {
        this.router.navigate(['/', PATH_RELEASE_PAGES]);
        return;
      }

      this.patientService.getPatient(patientId, user.isAdmin ? COLLECTION_PATIENTS : COLLECTION_STUDENT_PATIENTS)
        .subscribe(snapshot => {
          this.patientSignal.set(this.patientService.getPatientFromSnapshot(snapshot));
        });
    });

    // When patient loads, find and validate the provider order from the url
    effect(() => {
      const patient = this.patientSignal();
      if (!patient) return;

      const orderId = this.providerOrderId();
      if (!orderId) {
        this.router.navigate(['/', PATH_RELEASE_PAGES]);
        return;
      }

      const providerOrder = patient.providerOrders.find(po => po.id === orderId);
      if (!providerOrder) {
        this.router.navigate(['/', PATH_RELEASE_PAGES]);
        return;
      }

      this.providerOrderSignal.set(providerOrder);
    });

    // When provider order loads, find and open a matching container (once only)
    effect(() => {
      const providerOrder = this.providerOrderSignal();
      if (!providerOrder) return;

      firstValueFrom(this.containersService.getContainerByMedication(providerOrder.medId, providerOrder.quantity))
        .then(container => {
          if (!container) {
            this.openedContainerIdSignal.set(undefined);
            return;
          }

          this.containersService.openContainer(container.id);
          this.highlightedContainerId.set(container.id);
          if (container.quantity != undefined) {
            this.quantityRemaining = container.quantity - providerOrder.quantity;
            this.containerQuantity = container.quantity;
            this.containersService.setQuantity(container.id, container.quantity - providerOrder.quantity);
          }

          this.openedContainerIdSignal.set(container.id);
        });
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Read next-page params from the url (not reactive)
    const providerOrderIds = this.route.snapshot.queryParamMap.getAll(PATH_MORE_PROVIDER_ORDER_IDS);
    this.nextProviderOrderIdSignal.set(providerOrderIds.length > 0 ? providerOrderIds[0] : undefined);
    this.moreProviderOrderIdsSignal.set({ [PATH_MORE_PROVIDER_ORDER_IDS]: providerOrderIds.slice(1) });

    this.containersService.addAnyContainersOpenListener(anyOpen => { this.anyContainersOpen = anyOpen; });
  }

  closeContainer(): void {
    const id = this.openedContainerId();
    if (id !== undefined) {
      this.highlightedContainerId.set(undefined);
      this.containersService.closeContainer(id);
    }
  }
}
